import { BaseSyntheticEvent, useCallback, useMemo } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import { useFormContext, useWatch } from 'react-hook-form'
import { toast } from 'sonner'

import { CREATE_ACCOUNT_PATH } from '~/app/accounts/create/createRoutes'
import { AutomationFormValues } from '~/components/automations/automationSchema'
import { useAutomationName } from '~/components/automations/useAutomationName'
import { useSelectedBankAccount } from '~/components/automations/useSelectedBankAccount'
import { flattenPolicyForTracking } from '~/components/automations/utils/flattenPolicyForTracking'
import { Button } from '~/components/component-library'
import { QUERY_KEYS } from '~/constants/react-query'
import { useRequiredActiveOrg } from '~/hooks/org'
import { useBackNavigation } from '~/hooks/useBackNavigation'
import {
  passkeyConfirmationHeaders,
  usePasskeyConfirmation,
} from '~/hooks/usePasskeyConfirmation'
import { useServerRequests } from '~/hooks/useRequests'
import { useReturnPath } from '~/hooks/useReturnPath'
import { PasskeyConfirmationAction } from '~/types'
import { Automation, AutomationPolicy } from '~/types/automations'
import { DEFAULT_SLIPPAGE_BPS } from '~/utils/swap'

export const MINIMUM_THRESHOLD_USD = 5 // $5

export const SubmitButton = ({ automation }: { automation?: Automation }) => {
  const { id: orgId } = useRequiredActiveOrg()
  const { handleSubmit, control, formState } =
    useFormContext<AutomationFormValues>()
  const { serverPostRequest, serverPutRequest, isProcessing } =
    useServerRequests()
  const { confirmWithPasskey, isConfirming } = usePasskeyConfirmation()
  const goBack = useBackNavigation()
  const router = useRouter()
  // Cancel in create mode returns to the page that opened the create flow.
  const returnPath = useReturnPath({ defaultPath: CREATE_ACCOUNT_PATH })
  const queryClient = useQueryClient()

  const { name, generatedName } = useAutomationName()
  const swaps = useWatch({ control, name: 'swaps' })
  const destinationAddress = useWatch({ control, name: 'destinationAddress' })
  const selectedBankAccount = useSelectedBankAccount()

  const policy: AutomationPolicy = useMemo(
    () => ({
      version: 1,
      minimumThresholdUsd: MINIMUM_THRESHOLD_USD,
      slippage: DEFAULT_SLIPPAGE_BPS, // TODO: Calculate slippage per-swap rather than on the overarching automation policy
      passThroughPercent: selectedBankAccount
        ? 0
        : Math.max(0, 100 - swaps.reduce((sum, o) => sum + o.percent, 0)),
      passThroughDestinationAddress: destinationAddress,
      outputs: swaps.map((o) => ({
        percent: o.percent,
        targetChainId: o.targetChainId,
        targetTokenAddress: o.targetTokenAddress,
        destinationAddress: destinationAddress,
      })),
    }),
    [destinationAddress, selectedBankAccount, swaps],
  )

  const onSubmit = useCallback(async () => {
    if (automation) {
      const confirmationId = await confirmWithPasskey({
        action: PasskeyConfirmationAction.AutomationUpdate,
        orgId,
        resourceId: automation.id,
      })
      if (!confirmationId) return

      const response = await serverPutRequest<{ automation: Automation }>({
        path: `/api/org/automations/${automation.id}`,
        errorMessage: 'Failed to update automation',
        headers: passkeyConfirmationHeaders(confirmationId),
        params: {
          name: name || generatedName || undefined,
          policy,
        },
      })

      if (response?.data?.automation) {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.AUTOMATIONS],
        })
        queryClient.setQueryData([QUERY_KEYS.AUTOMATION, automation.id], {
          automation: response.data.automation,
        })
        toast.success('Automation updated')
        goBack()
      }
    } else {
      const response = await serverPostRequest<{ automation: Automation }>({
        path: '/api/org/automations',
        errorMessage: 'Failed to create automation',
        params: {
          orgId,
          name: name || generatedName || undefined,
          policy,
        },
      })

      if (response?.data?.automation) {
        toast.success('Automation created')
        const { accountAddress } = response.data.automation
        // Land on the new automation's account page. accountAddress is
        // typed nullable, so fall back to the accounts list.
        router.push(
          accountAddress
            ? `/accounts/${accountAddress}`
            : '/accounts/?tab=automation',
        )
      }
    }
  }, [
    automation,
    confirmWithPasskey,
    generatedName,
    name,
    goBack,
    orgId,
    policy,
    queryClient,
    router,
    serverPostRequest,
    serverPutRequest,
  ])

  const submitHandler = useCallback(
    (event: BaseSyntheticEvent) => {
      handleSubmit(onSubmit, () => {
        toast.error('Invalid automation', {
          description: 'Please review the form',
        })
      })(event)
    },
    [handleSubmit, onSubmit],
  )

  const isDisabled = !formState.isValid

  const isEditMode = !!automation

  return (
    <div className="flex w-full flex-col space-y-4">
      <div className="flex w-full items-center justify-between">
        <Button
          type="button"
          variant="secondary"
          onClick={() => (isEditMode ? goBack() : router.push(returnPath))}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          onClick={submitHandler}
          shortcut={{
            key: 'meta+enter',
            callback: (e) => {
              e.preventDefault()
              // Prevent the form from being submitted if a dialog is open (e.g. the token swap dialog)
              if (document.querySelector('[role="dialog"]')) return
              submitHandler(e as unknown as BaseSyntheticEvent)
            },
          }}
          isDisabled={isDisabled}
          isLoading={isProcessing || isConfirming}
          eventName={
            isEditMode ? 'clickedUpdateAutomation' : 'clickedCreateAutomation'
          }
          eventProperties={{
            orgId,
            name: name || generatedName,
            isBankAccount: !!selectedBankAccount,
            ...flattenPolicyForTracking(policy),
          }}
        >
          {isEditMode ? 'Save' : 'Create'}
        </Button>
      </div>
    </div>
  )
}
